import React, { useState, useEffect, useContext, useMemo } from 'react'
import AppLayout from '../layouts/AppLayout'
import { AppContext } from '../contexts/AppContext'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { FiPlus, FiSearch, FiX, FiEdit, FiClock, FiArrowDown, FiArrowUp } from 'react-icons/fi'
import Swal from 'sweetalert2'
import { FaFileExcel } from 'react-icons/fa'
import { Bar } from 'react-chartjs-2'
import 'chart.js/auto'
import { addDays, format, startOfDay, subDays } from 'date-fns'

const X_RESERVED_PATHS = ['i', 'home', 'search', 'explore', 'hashtag', 'intent', 'share', 'settings', 'messages', 'notifications']

// Mirrors the API: https://x.com/MilanZak7/status/2099919496383533409 -> https://x.com/MilanZak7/
const toXAccountUrl = (input) => {
  let value = (input || '').trim()
  if (!value) return null

  const bareHandle = value.match(/^@?([A-Za-z0-9_]{1,15})$/)
  let handle = bareHandle ? bareHandle[1] : null

  if (!handle) {
    if (!/^https?:\/\//i.test(value)) value = 'https://' + value
    let parsed
    try {
      parsed = new URL(value)
    } catch (e) {
      return null
    }
    const host = parsed.hostname.toLowerCase().replace(/^(www\.|mobile\.)/, '')
    if (host !== 'x.com' && host !== 'twitter.com') return null
    const firstSegment = parsed.pathname.split('/').filter(Boolean)[0] || ''
    if (!/^[A-Za-z0-9_]{1,15}$/.test(firstSegment)) return null
    handle = firstSegment
  }

  if (X_RESERVED_PATHS.includes(handle.toLowerCase())) return null
  return `https://x.com/${handle}/`
}

// Date <-> <input type='datetime-local'> value, in the viewer's local time
const toInputValue = (date) => {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const fromInputValue = (value) => (value ? new Date(value).toISOString() : null)

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const inputClass =
  'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all'

const ModalShell = ({ isOpen, onClose, title, maxWidth = 'max-w-2xl', children }) => (
  <Transition appear show={isOpen} as={Fragment}>
    <Dialog as='div' className='relative z-50' onClose={onClose}>
      <Transition.Child
        as={Fragment}
        enter='ease-out duration-300'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='ease-in duration-200'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'
      >
        <div className='fixed inset-0 hidden bg-gray-500 bg-opacity-25 blur-xl transition-opacity md:block' />
      </Transition.Child>

      <div className='fixed inset-0 overflow-y-auto'>
        <div className='flex min-h-full items-center justify-center p-4 text-center'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'
          >
            <Dialog.Panel
              className={`w-full ${maxWidth} transform overflow-hidden rounded-2xl bg-white p-8 text-left align-middle shadow-xl transition-all`}
            >
              <div className='flex items-center justify-between mb-6'>
                <Dialog.Title as='h3' className='text-xl font-semibold leading-6 text-gray-900'>
                  {title}
                </Dialog.Title>
                <button onClick={onClose} className='text-gray-400 hover:text-gray-600 transition-colors'>
                  <FiX size={20} />
                </button>
              </div>
              {children}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </div>
    </Dialog>
  </Transition>
)

const ModalFooter = ({ onCancel, isLoading, submitLabel }) => (
  <div className='mt-8 flex justify-end gap-3 pt-6 border-t border-gray-200'>
    <button
      type='button'
      onClick={onCancel}
      className='px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-all'
    >
      Cancel
    </button>
    <button
      type='submit'
      disabled={isLoading}
      className='px-5 py-2.5 btn-main-inverse text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all shadow-sm hover:shadow-md'
    >
      {isLoading ? 'Saving...' : submitLabel}
    </button>
  </div>
)

const thClass = 'px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'

const SORT_TITLES = {
  noticed: { desc: 'Newest first', asc: 'Oldest first' },
  taken_down: { desc: 'Taken down, newest first', asc: 'Not taken down, then oldest' },
}

// Module level so re-renders keep the same button node; a remount between
// mousedown and mouseup would swallow the click
const SortableTh = ({ column, sort, onSort, children }) => {
  const isActive = sort.column === column
  const nextOrder = isActive && sort.order === 'desc' ? 'asc' : 'desc'
  return (
    <th className={thClass}>
      <button
        type='button'
        onClick={() => onSort(column)}
        title={`Sort: ${SORT_TITLES[column][nextOrder]}`}
        className='inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-900'
      >
        {children}
        {isActive ? (
          sort.order === 'desc' ? <FiArrowDown size={14} /> : <FiArrowUp size={14} />
        ) : (
          <FiArrowDown size={14} className='opacity-30' />
        )}
      </button>
      {isActive && (
        <div className='text-[10px] font-normal normal-case tracking-normal text-gray-500'>
          {SORT_TITLES[column][sort.order]}
        </div>
      )}
    </th>
  )
}

const STATS_RANGES = [7, 14, 30, 90]
const TAKEN_DOWN_COLOR = '#0f9d58'
const NOTICED_COLOR = '#2f8fd8'

// Accounts noticed / taken down per day, counted in the viewer's timezone
const IptvStatsChart = ({ getIptvStats, refreshKey }) => {
  const [days, setDays] = useState(30)
  const [stats, setStats] = useState({ noticed: [], takenDown: [] })

  const from = useMemo(() => startOfDay(subDays(new Date(), days - 1)), [days])

  useEffect(() => {
    getIptvStats(from, new Date()).then(setStats)
  }, [getIptvStats, from, refreshKey])

  const chartData = useMemo(() => {
    const dayKeys = Array.from({ length: days }, (_, i) => format(addDays(from, i), 'yyyy-MM-dd'))
    const countPerDay = (dates) => {
      const counts = Object.fromEntries(dayKeys.map((key) => [key, 0]))
      dates.forEach((date) => {
        const key = format(new Date(date), 'yyyy-MM-dd')
        if (key in counts) counts[key] += 1
      })
      return dayKeys.map((key) => counts[key])
    }

    return {
      labels: dayKeys.map((key) => format(new Date(`${key}T00:00`), 'dd/MM')),
      datasets: [
        {
          label: 'Taken down',
          data: countPerDay(stats.takenDown),
          backgroundColor: TAKEN_DOWN_COLOR,
          borderRadius: 4,
        },
        {
          label: 'Noticed',
          data: countPerDay(stats.noticed),
          backgroundColor: NOTICED_COLOR,
          borderRadius: 4,
        },
      ],
    }
  }, [stats, days, from])

  const options = {
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'top', align: 'end' } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  }

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4'>
      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3'>
        <div className='flex items-center gap-6 flex-wrap'>
          <div>
            <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Taken down</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.takenDown.length}</p>
          </div>
          <div>
            <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Noticed</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.noticed.length}</p>
          </div>
          <p className='text-sm text-gray-500'>in the last {days} days</p>
        </div>
        <div className='flex items-center gap-1 rounded-lg border border-gray-300 p-1'>
          {STATS_RANGES.map((range) => (
            <button
              key={range}
              onClick={() => setDays(range)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                days === range ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {range}d
            </button>
          ))}
        </div>
      </div>
      <div className='h-72'>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  )
}

const IptvAccounts = () => {
  const {
    userToken,
    getIptvAccounts,
    getIptvStats,
    downloadiptvaccountsexcel,
    addIptvAccounts,
    updateIptvAccount,
    setIptvAccountsTakenDown,
    deleteIptvAccount,
    isLoading,
  } = useContext(AppContext)
  const isAdmin = userToken?.type === 'Admin'

  const [accounts, setAccounts] = useState([])
  const [pagination, setPagination] = useState({})
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedIds, setSelectedIds] = useState([])
  const [statsRefreshKey, setStatsRefreshKey] = useState(0)
  const [sort, setSort] = useState({ column: 'noticed', order: 'desc' })
  const [isExporting, setIsExporting] = useState(false)

  // Add tweets modal
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [tweetUrls, setTweetUrls] = useState('')
  const [noticedAt, setNoticedAt] = useState('')

  // Taken down modal (one or many rows)
  const [takenDownIds, setTakenDownIds] = useState(null)
  const [takenDownAt, setTakenDownAt] = useState('')

  // Edit modal
  const [editingAccount, setEditingAccount] = useState(null)
  const [editUrl, setEditUrl] = useState('')
  const [editNoticedAt, setEditNoticedAt] = useState('')
  const [editTakenDownAt, setEditTakenDownAt] = useState('')

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
      setCurrentPage(1)
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  useEffect(() => {
    fetchAccounts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, debouncedSearchQuery, statusFilter, sort])

  const fetchAccounts = async () => {
    const data = await getIptvAccounts(
      currentPage,
      itemsPerPage,
      debouncedSearchQuery,
      statusFilter,
      sort.column,
      sort.order,
    )
    setAccounts(data.accounts)
    setPagination(data.pagination)
    setSelectedIds([])
    setStatsRefreshKey((key) => key + 1)
  }

  // Live preview of the tweet -> account conversion in the add modal
  const preview = useMemo(() => {
    const lines = tweetUrls
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
    const accountUrls = []
    const invalid = []
    lines.forEach((line) => {
      const accountUrl = toXAccountUrl(line)
      if (!accountUrl) invalid.push(line)
      else if (!accountUrls.some((u) => u.toLowerCase() === accountUrl.toLowerCase())) accountUrls.push(accountUrl)
    })
    return { lines, accountUrls, invalid }
  }, [tweetUrls])

  const handleAddClick = () => {
    setTweetUrls('')
    setNoticedAt(toInputValue(new Date()))
    setIsAddOpen(true)
  }

  const handleAddSubmit = async (e) => {
    e.preventDefault()

    if (preview.accountUrls.length === 0) {
      Swal.fire('Error', 'Please paste at least one valid X URL', 'error')
      return
    }

    const result = await addIptvAccounts({ urls: preview.lines, noticed_at: fromInputValue(noticedAt) })
    if (result) {
      setIsAddOpen(false)
      if (result.invalidUrls?.length) {
        Swal.fire('Some URLs were skipped', result.invalidUrls.join('<br/>'), 'warning')
      }
      if (currentPage === 1) fetchAccounts()
      else setCurrentPage(1)
    }
  }

  const openTakenDown = (ids) => {
    setTakenDownIds(ids)
    setTakenDownAt(toInputValue(new Date()))
  }

  const handleTakenDownSubmit = async (e) => {
    e.preventDefault()
    if (!takenDownAt) return

    const result = await setIptvAccountsTakenDown({ ids: takenDownIds, taken_down_at: fromInputValue(takenDownAt) })
    if (result) {
      setTakenDownIds(null)
      fetchAccounts()
    }
  }

  const handleEditClick = (account) => {
    setEditingAccount(account)
    setEditUrl(account.account_url)
    setEditNoticedAt(toInputValue(account.noticed_at))
    setEditTakenDownAt(toInputValue(account.taken_down_at))
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()

    const result = await updateIptvAccount({
      id: editingAccount.id,
      url: editUrl !== editingAccount.account_url ? editUrl : undefined,
      noticed_at: fromInputValue(editNoticedAt),
      taken_down_at: fromInputValue(editTakenDownAt),
    })
    if (result) {
      setEditingAccount(null)
      fetchAccounts()
    }
  }

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure to delete?',
      text: "You won't be able to undo this action",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Delete',
    })

    if (result.isConfirmed) {
      await deleteIptvAccount(id)
      fetchAccounts()
    }
  }

  const toggleSelected = (id) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]))
  }

  const allSelected = accounts.length > 0 && selectedIds.length === accounts.length
  const toggleAll = () => setSelectedIds(allSelected ? [] : accounts.map((a) => a.id))

  // Exports every page, with the current search, filter and sort
  const handleExportExcel = async () => {
    if (isExporting) return
    setIsExporting(true)
    await downloadiptvaccountsexcel({
      search: debouncedSearchQuery,
      status: statusFilter,
      sort: sort.column,
      order: sort.order,
    })
    setIsExporting(false)
  }

  // Clicking the active column flips the order; a new column starts at desc
  const handleSort = (column) => {
    setSort((current) =>
      current.column === column
        ? { column, order: current.order === 'desc' ? 'asc' : 'desc' }
        : { column, order: 'desc' },
    )
    setCurrentPage(1)
  }

  return (
    <AppLayout pageName='IPTV'>
      <div className='max-w-[95%] mx-auto p-6 space-y-6'>
        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
          <div>
            <h1 className='text-3xl font-bold text-gray-900'>IPTV</h1>
            <p className='text-sm text-gray-500 mt-1'>X accounts reported for advertising IPTV</p>
          </div>
          <div className='flex items-center gap-3 flex-wrap'>
            {isAdmin && selectedIds.length > 0 && (
              <button
                onClick={() => openTakenDown(selectedIds)}
                className='flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm hover:shadow-md'
              >
                <FiClock size={18} />
                Set taken down ({selectedIds.length})
              </button>
            )}
            <button
              onClick={handleExportExcel}
              disabled={isExporting}
              className='flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md'
            >
              <FaFileExcel size={18} />
              {isExporting ? 'Exporting...' : 'Export Excel'}
            </button>
            {isAdmin && (
              <button
                onClick={handleAddClick}
                className='flex items-center gap-2 px-5 py-2.5 btn-main-inverse text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md'
              >
                <FiPlus size={18} />
                Add Tweets
              </button>
            )}
          </div>
        </div>

        <IptvStatsChart
          getIptvStats={getIptvStats}
          refreshKey={statsRefreshKey}
        />

        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row gap-3'>
          <div className='relative flex-1'>
            <FiSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' size={18} />
            <input
              type='text'
              placeholder='Search by account or tweet URL...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`${inputClass} pl-10`}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setCurrentPage(1)
            }}
            className='border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white'
          >
            <option value='all'>All accounts</option>
            <option value='active'>Still active</option>
            <option value='taken_down'>Taken down</option>
          </select>
        </div>

        <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
          {isLoading && accounts.length === 0 ? (
            <div className='text-center py-16'>
              <div className='animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto'></div>
              <p className='mt-3 text-gray-600 font-medium'>Loading accounts...</p>
            </div>
          ) : accounts.length > 0 ? (
            <>
              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      {isAdmin && (
                        <th className='pl-6 py-4 w-4'>
                          <input type='checkbox' checked={allSelected} onChange={toggleAll} />
                        </th>
                      )}
                      <th className={thClass}>Account</th>
                      <SortableTh
                        column='noticed'
                        sort={sort}
                        onSort={handleSort}
                      >
                        Noticed
                      </SortableTh>
                      <SortableTh
                        column='taken_down'
                        sort={sort}
                        onSort={handleSort}
                      >
                        Taken down
                      </SortableTh>
                      {isAdmin && <th className={thClass}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {accounts.map((account) => (
                      <tr key={account.id} className='hover:bg-gray-50 transition-colors'>
                        {isAdmin && (
                          <td className='pl-6 py-4'>
                            <input
                              type='checkbox'
                              checked={selectedIds.includes(account.id)}
                              onChange={() => toggleSelected(account.id)}
                            />
                          </td>
                        )}
                        <td className='px-6 py-4'>
                          <div className='flex items-center gap-2'>
                            <img width='20' height='20' src='/images/icons/x.png' alt='X' className='object-contain' />
                            <a
                              href={account.account_url}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-blue-600 hover:text-blue-800 hover:underline break-all text-sm font-medium'
                            >
                              {account.account_url}
                            </a>
                          </div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>{formatDate(account.noticed_at)}</td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm'>
                          {account.taken_down_at ? (
                            <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                              {formatDate(account.taken_down_at)}
                            </span>
                          ) : isAdmin ? (
                            <button
                              onClick={() => openTakenDown([account.id])}
                              className='inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-gray-300 text-gray-700 hover:bg-gray-100'
                            >
                              <FiClock size={14} /> Set time
                            </button>
                          ) : (
                            <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800'>
                              Active
                            </span>
                          )}
                        </td>
                        {isAdmin && (
                          <td className='px-6 py-4 whitespace-nowrap'>
                            <div className='flex items-center gap-3'>
                              <button
                                onClick={() => handleEditClick(account)}
                                className='p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors'
                                title='Edit'
                              >
                                <FiEdit size={18} />
                              </button>
                              <button
                                onClick={() => handleDelete(account.id)}
                                className='p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors'
                                title='Delete'
                              >
                                <FiX size={18} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className='bg-gray-50 border-t border-gray-200 px-6 py-4'>
                <div className='flex flex-col sm:flex-row justify-between items-center gap-4'>
                  <div className='flex items-center gap-4 flex-wrap'>
                    <div className='flex items-center gap-2'>
                      <label htmlFor='itemsPerPage' className='text-sm font-medium text-gray-700'>
                        Rows per page:
                      </label>
                      <select
                        id='itemsPerPage'
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(parseInt(e.target.value))
                          setCurrentPage(1)
                        }}
                        className='border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white'
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                    <div className='text-sm text-gray-600'>
                      <span className='font-semibold text-gray-700'>{pagination.totalItems}</span> total records
                    </div>
                  </div>

                  {pagination.totalPages > 1 && (
                    <div className='flex items-center gap-3'>
                      <div className='text-sm text-gray-600'>
                        Page <span className='font-semibold text-gray-700'>{pagination.currentPage || 1}</span> of{' '}
                        <span className='font-semibold text-gray-700'>{pagination.totalPages || 1}</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <button
                          onClick={() => setCurrentPage((pagination.currentPage || 1) - 1)}
                          disabled={!pagination.hasPrevPage}
                          className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white hover:border-gray-400 transition-all bg-white'
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage((pagination.currentPage || 1) + 1)}
                          disabled={!pagination.hasNextPage}
                          className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white hover:border-gray-400 transition-all bg-white'
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className='text-center py-16'>
              <div className='text-gray-400 mb-2'>
                <FiSearch size={48} className='mx-auto opacity-50' />
              </div>
              <p className='text-gray-500 font-medium'>No IPTV accounts found</p>
              <p className='text-sm text-gray-400 mt-1'>
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter'
                  : isAdmin
                  ? 'Get started by adding tweets'
                  : ''}
              </p>
            </div>
          )}
        </div>

        <ModalShell isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} title='Add Tweets'>
          <form onSubmit={handleAddSubmit}>
            <div className='space-y-5'>
              <div>
                <label className='block text-sm font-semibold text-gray-700 mb-2'>Tweet URLs (one per line)</label>
                <textarea
                  value={tweetUrls}
                  onChange={(e) => setTweetUrls(e.target.value)}
                  rows={6}
                  className={`${inputClass} resize-none`}
                  placeholder='https://x.com/MilanZak7/status/2099919496383533409'
                  required
                />
              </div>

              {preview.lines.length > 0 && (
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm max-h-48 overflow-y-auto'>
                  <p className='font-semibold text-gray-700 mb-1'>
                    {preview.accountUrls.length} account(s) will be added
                  </p>
                  {preview.accountUrls.map((u) => (
                    <div key={u} className='text-gray-600 break-all'>
                      {u}
                    </div>
                  ))}
                  {preview.invalid.map((u) => (
                    <div key={u} className='text-red-600 break-all'>
                      Not an X URL: {u}
                    </div>
                  ))}
                </div>
              )}

              <div>
                <label className='block text-sm font-semibold text-gray-700 mb-2'>Noticed time</label>
                <input
                  type='datetime-local'
                  value={noticedAt}
                  onChange={(e) => setNoticedAt(e.target.value)}
                  className={inputClass}
                  required
                />
              </div>
            </div>
            <ModalFooter onCancel={() => setIsAddOpen(false)} isLoading={isLoading} submitLabel='Add Accounts' />
          </form>
        </ModalShell>

        <ModalShell
          isOpen={takenDownIds !== null}
          onClose={() => setTakenDownIds(null)}
          title={takenDownIds?.length > 1 ? `Set taken down time (${takenDownIds.length} accounts)` : 'Set taken down time'}
          maxWidth='max-w-md'
        >
          <form onSubmit={handleTakenDownSubmit}>
            <label className='block text-sm font-semibold text-gray-700 mb-2'>Taken down time</label>
            <input
              type='datetime-local'
              value={takenDownAt}
              onChange={(e) => setTakenDownAt(e.target.value)}
              className={inputClass}
              required
            />
            <ModalFooter onCancel={() => setTakenDownIds(null)} isLoading={isLoading} submitLabel='Save' />
          </form>
        </ModalShell>

        <ModalShell isOpen={editingAccount !== null} onClose={() => setEditingAccount(null)} title='Edit IPTV Account'>
          <form onSubmit={handleEditSubmit}>
            <div className='space-y-5'>
              <div>
                <label className='block text-sm font-semibold text-gray-700 mb-2'>Account URL</label>
                <input
                  type='text'
                  value={editUrl}
                  onChange={(e) => setEditUrl(e.target.value)}
                  className={inputClass}
                  required
                />
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-semibold text-gray-700 mb-2'>Noticed time</label>
                  <input
                    type='datetime-local'
                    value={editNoticedAt}
                    onChange={(e) => setEditNoticedAt(e.target.value)}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className='block text-sm font-semibold text-gray-700 mb-2'>Taken down time</label>
                  <input
                    type='datetime-local'
                    value={editTakenDownAt}
                    onChange={(e) => setEditTakenDownAt(e.target.value)}
                    className={inputClass}
                  />
                  <p className='text-xs text-gray-400 mt-1'>Leave empty if the account is still active</p>
                </div>
              </div>
            </div>
            <ModalFooter onCancel={() => setEditingAccount(null)} isLoading={isLoading} submitLabel='Update Account' />
          </form>
        </ModalShell>
      </div>
    </AppLayout>
  )
}

export default IptvAccounts
